import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { bookmarkKeys, getTestSamplePicker, setDefaultBookmark } from '../../entities/bookmark';

/** "Test with" in the editor: the flow's source bookmarks (default first) and its newest 20. */
export const useTestSamplePicker = (flowId: string, enabled = true) =>
  useQuery({
    queryKey: bookmarkKeys.picker(flowId),
    queryFn: () => getTestSamplePicker(flowId),
    enabled,
  });

/** Older requests, 20 a page, from where the first page stopped — only once asked for. */
export const useOlderSamples = (flowId: string, from: string | null, enabled: boolean) =>
  useInfiniteQuery({
    queryKey: [...bookmarkKeys.picker(flowId), 'older', from],
    queryFn: ({ pageParam }) => getTestSamplePicker(flowId, { before: pageParam }),
    initialPageParam: from ?? '',
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    enabled: enabled && from !== null,
  });

/** One request of the source by its id, however old: "req_…" typed into the search. */
export const useSampleById = (flowId: string, requestId: string | null) =>
  useQuery({
    queryKey: [...bookmarkKeys.picker(flowId), 'id', requestId],
    queryFn: () => getTestSamplePicker(flowId, { requestId: requestId ?? '' }),
    enabled: requestId !== null,
  });

/** "Use as the test sample in …": one flow's default; `bookmarkId: null` clears it. */
export const useSetDefaultBookmark = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ flowId, bookmarkId }: { flowId: string; bookmarkId: string | null }) =>
      setDefaultBookmark(flowId, bookmarkId),
    onSuccess: (_saved, { flowId }) =>
      Promise.all([
        client.invalidateQueries({ queryKey: bookmarkKeys.all }),
        client.invalidateQueries({ queryKey: ['flows', flowId] }),
      ]),
  });
};
